export const VOID_ACTION = {
  review: "review",
  assign: "assign",
  decline: "decline",
  airlineDeclined: "airlineDeclined",
  voidTicket: "voidTicket",
  manualVoid: "manualVoid",
  remark: "remark",
  messages: "messages",
  supplierResponse: "supplierResponse",
};

/** Hardcoded rejectReason so the agent tracker can treat airline declines specially. */
export const VOID_AIRLINE_DECLINED_REASON = "AIRLINE_DECLINED";

function normalizeVoidStatus(status) {
  return String(status || "")
    .replace(/[_\s-]/g, "")
    .toLowerCase();
}

function voidStatusNote(data) {
  return String(
    data?.manualInterventionReason ||
      data?.lastError ||
      data?.errorMessage ||
      data?.rejectReason ||
      "",
  );
}

export function isVoidManualReviewStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "manualreviewrequired" ||
    normalized === "voidrequestsubmitted"
  );
}

export function isAutoVoidFailedStatus(status) {
  return normalizeVoidStatus(status) === "autovoidfailed";
}

export function isVoidAlreadyAccepted(data) {
  return (
    Boolean(data?.approvedByAdmin) ||
    isManualVoidProcessingStatus(data?.status)
  );
}

export function isVoidTechnicalSendFailure(data) {
  const kind = String(data?.lastErrorKind || "").toLowerCase();
  if (kind === "technical") return true;
  return /technical error|api key|timeout|unavailable/i.test(
    voidStatusNote(data),
  );
}

export function isVoidPendingStatus(status) {
  return (
    status === "Pending" ||
    isVoidManualReviewStatus(status) ||
    isAutoVoidFailedStatus(status)
  );
}

export function isManualVoidProcessingStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return normalized === "manualvoidprocessing" || normalized === "approved";
}

export function isAutoVoidProcessingStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "autovoidprocessing" || normalized === "voidprocessing"
  );
}

export function isVoidSubmittedToSupplierStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "submittedtosupplier" ||
    normalized === "supplierprocessing"
  );
}

export function isVoidSuccessfulStatus(status) {
  return normalizeVoidStatus(status) === "voidsuccessful";
}

export function isVoidSettlementPendingStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "settlementpending" ||
    normalized === "servicefeedebited" ||
    normalized === "balancerefunded"
  );
}

export function isVoidCompletedStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "voidcompleted" ||
    normalized === "success" ||
    normalized === "closed"
  );
}

export function isVoidSupplierFailedStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "voidfailedbysupplier" ||
    normalized === "voiddeclinedbysupplier"
  );
}

export function isVoidFailureSettledStatus(status) {
  return normalizeVoidStatus(status) === "voidfailuresettled";
}

export function isVoidSettlementFailedStatus(status) {
  return normalizeVoidStatus(status) === "settlementfailed";
}

export function isVoidValidationFailedStatus(status) {
  return normalizeVoidStatus(status) === "voidrequestvalidationfailed";
}

export function needsVoidReview(data) {
  if (!data || isVoidAlreadyAccepted(data)) return false;
  return (
    data?.status === "Pending" ||
    isVoidManualReviewStatus(data?.status) ||
    isAutoVoidFailedStatus(data?.status)
  );
}

export function isVoidAutoProcessingMethod(data) {
  return String(data?.processingMethod || "").toLowerCase() === "auto";
}

/** Retry PATCH /voidTicket/void/:id — Auto voids stuck on ManualReviewRequired. */
export function canRetryAutoVoid(data) {
  if (!data || !isVoidAutoProcessingMethod(data)) return false;
  return normalizeVoidStatus(data?.status) === "manualreviewrequired";
}

/** @deprecated Prefer canRetryAutoVoid — kept for older call sites. */
export function canSubmitVoidToAirline(data) {
  return canRetryAutoVoid(data);
}

export function canManualVoidTicket(data) {
  const stage = getVoidAdminStage(data);
  return [
    "needs_manual_void",
    "send_failed",
    "auto_processing",
    "awaiting_supplier",
    "settlement_pending",
    "settlement_failed",
    "supplier_failed",
  ].includes(stage);
}

export function isVoidWalletCreditStage(data) {
  const stage = getVoidAdminStage(data);
  return stage === "settlement_pending" || stage === "settlement_failed";
}

export function isVoidCloseRequestStage(data) {
  const stage = getVoidAdminStage(data);
  return stage === "supplier_failed" || stage === "validation_failed";
}

export function canDeclineVoidRequest(data) {
  const stage = getVoidAdminStage(data);
  return [
    "needs_manual_void",
    "send_failed",
    "auto_processing",
    "needs_manual_review",
    "supplier_failed",
    "validation_failed",
  ].includes(stage);
}

/** Same stages as Ops decline — but records airline said no. */
export function canAirlineDeclineVoid(data) {
  if (isVoidCloseRequestStage(data)) return false;
  return canDeclineVoidRequest(data);
}

export function isVoidAirlineDeclinedReason(data) {
  const reason = String(
    data?.rejectReason || data?.reason || "",
  )
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return (
    reason === VOID_AIRLINE_DECLINED_REASON ||
    reason === "AIRLINE_DECLINED"
  );
}

export function canAssignVoidRequest(data) {
  const stage = getVoidAdminStage(data);
  return !["completed", "declined", "failure_settled", "canceled"].includes(
    stage,
  );
}

export function canAddVoidRemark(data) {
  return canAssignVoidRequest(data);
}

export function getVoidSubmitActionLabel() {
  return "Retry auto submission";
}

export function getVoidRetryAutoConfirmLabel() {
  return "Yes, retry automatic void";
}

export function getVoidRetryAutoLoadingTitle() {
  return "Retrying automatic void submission";
}

export function getVoidManualActionLabel(data) {
  return isVoidWalletCreditStage(data)
    ? "Credit agent wallet"
    : "Already voided on airline — finish here";
}

export function getVoidDeclineActionLabel(data) {
  return isVoidCloseRequestStage(data)
    ? "Close this request"
    : "Decline request";
}

export function getVoidAirlineDeclinedActionLabel() {
  return "Airline declined";
}

export function getVoidAssignActionLabel(data) {
  return data?.tasks?.length ? "Reassign" : "Assign to teammate";
}

/** Shared “how to void on the airline site” steps for manual send stages. */
const VOID_AIRLINE_SUBMIT_STEPS = [
  "Open the airline or supplier website (portal / GDS).",
  "Find this ticket or PNR.",
  "Submit the void there yourself. That is the real void — Intraverse has not voided it yet.",
  "Confirm on the airline site that the ticket shows voided.",
  "Come back here.",
  'Click "Already voided on airline — finish here". That finishes the request and pays the agent.',
];

const VOID_AIRLINE_CHECK_ONLY_STEPS = [
  "Do not submit a new void on the airline site.",
  "You may open the airline site only to check the ticket status.",
  "Wait for the airline answer in Intraverse.",
  'If this is stuck and the airline already shows voided, use "Already voided on airline — finish here".',
  "Do not credit the agent wallet yet.",
];

export function isVoidRequestDeniedStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "voidrequestdenied" ||
    normalized === "denied" ||
    normalized === "rejected" ||
    normalized === "cancelled" ||
    normalized === "canceled"
  );
}

export function isVoidRequestCanceledStatus(status) {
  const normalized = normalizeVoidStatus(status);
  return (
    normalized === "voidrequestcanceled" ||
    normalized === "voidrequestcancelled"
  );
}

export function hasVoidSupplierResponse(data) {
  const response = data?.supplierResponse;
  if (response == null) return false;
  if (typeof response === "object") {
    return Object.keys(response).length > 0;
  }
  return true;
}

export function getVoidAdminStage(data) {
  const status = data?.status;
  const code = normalizeVoidStatus(status);

  if (isVoidRequestCanceledStatus(status)) {
    return "canceled";
  }
  if (isVoidRequestDeniedStatus(status)) {
    return "declined";
  }
  if (status === "Failed") return "supplier_failed";
  if (isVoidValidationFailedStatus(status)) return "validation_failed";
  if (isVoidFailureSettledStatus(status)) return "failure_settled";
  if (isVoidSettlementFailedStatus(status)) return "settlement_failed";
  if (isVoidSupplierFailedStatus(status)) return "supplier_failed";
  if (isVoidCompletedStatus(status)) return "completed";
  if (isVoidSuccessfulStatus(status) || isVoidSettlementPendingStatus(status)) {
    return "settlement_pending";
  }
  if (isVoidSubmittedToSupplierStatus(status)) return "awaiting_supplier";
  if (isAutoVoidProcessingStatus(status)) return "auto_processing";
  if (isAutoVoidFailedStatus(status)) return "send_failed";
  if (isManualVoidProcessingStatus(status)) return "needs_manual_void";
  if (isVoidAlreadyAccepted(data) && isVoidManualReviewStatus(status)) {
    return isVoidTechnicalSendFailure(data)
      ? "send_failed"
      : "needs_manual_void";
  }
  if (
    isVoidManualReviewStatus(status) ||
    status === "Pending" ||
    status === "PendingVoid" ||
    code === "pending"
  ) {
    return "needs_manual_review";
  }
  return "unknown";
}

export function getVoidReviewTrigger(data) {
  const code = normalizeVoidStatus(data?.status);
  if (code === "voidrequestvalidationfailed") return "validation_failed";
  if (code === "autovoidfailed") return "auto_failed";
  if (code === "voidrequestsubmitted") return "request_submitted";
  if (code === "manualreviewrequired" || code === "pending" || code === "pendingvoid") {
    if (isVoidTechnicalSendFailure(data) || isVoidAlreadyAccepted(data)) {
      return "auto_failed";
    }
    return "needs_review";
  }
  return "needs_review";
}

export function getVoidReviewGuidance(data) {
  const trigger = getVoidReviewTrigger(data);
  const note = voidStatusNote(data);
  const accepted = isVoidAlreadyAccepted(data);
  const statusDisplay =
    getVoidStatusLabelForCode(data?.status) || data?.status || "Unknown";

  const shared = {
    trigger,
    note,
    statusDisplay,
    whyTitle: "Why you're here",
    showAccept: false,
    showDecline: true,
    showSubmit: false,
    showManual: false,
    acceptLabel: "Accept — then I will void on airline",
    declineLabel: "Decline",
    submitLabel: getVoidSubmitActionLabel(),
    manualLabel: getVoidManualActionLabel(data),
  };

  const copies = {
    validation_failed: {
      title: "This void could not be started",
      why: "A system check failed before anything was sent. Ticket missing, wrong agent, already voided/refunded, outside void window, or another void is already active. No money moved.",
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "The agent can submit again if the ticket is still eligible.",
      ],
      questions: ["Which check failed?"],
      declineLabel: "Close this request",
    },
    request_submitted: {
      title: "Void request received",
      why: "Validation passed. Next is automatic or manual processing. If you are reviewing it, check whether this ticket can still be voided.",
      steps: [
        "On this screen: check ticket number, passengers, and void deadline.",
        "If you need to confirm eligibility: open the airline site and CHECK only — do not void yet.",
        "If it can be voided: Accept. After that you will submit the void on the airline site.",
        "If it cannot: Decline and write a plain-language reason.",
      ],
      questions: [
        "Is this the right issued ticket?",
        "Is it still inside the void window?",
      ],
      showAccept: true,
    },
    auto_failed: {
      title: "Automatic void send failed",
      why: accepted
        ? "Intraverse tried to send the void automatically and hit a technical problem (timeout, API down, auth, bad response). That is not an airline decline. The ticket is not voided yet — void it on the airline site, then finish here."
        : "Intraverse tried to send the void automatically and hit a technical problem (timeout, API down, auth, bad response). That is not an airline decline. The ticket is not voided yet. Accept first, then void it on the airline site.",
      steps: accepted
        ? VOID_AIRLINE_SUBMIT_STEPS
        : [
            "On this screen: check ticket number, passengers, and void deadline.",
            "Open the airline site only to CHECK eligibility if needed — do not void yet.",
            "If it can be voided: Accept. Next you will void it on the airline site and finish here.",
            "If it cannot: Decline and write a plain-language reason.",
          ],
      questions: [
        "Did our send fail, or did the airline actually refuse?",
        "A timeout is not an airline no.",
      ],
      showAccept: !accepted,
      showSubmit: false,
      // "Already voided" only after Accept — not on the first review.
      showManual: accepted,
    },
    needs_review: {
      title: "Ops must review this void",
      why: "A person must handle this. Either this supplier has no automatic void, or Ops must confirm the ticket can still be voided. Accepting is not a void yet — you have not submitted anything to the airline.",
      steps: [
        "On this screen: check ticket number, passengers, and void deadline.",
        "If you need to confirm eligibility: open the airline site and CHECK only — do not void yet.",
        "If it can be voided: Accept. Next status will tell you to submit the void on the airline site.",
        "If it cannot: Decline and write a plain-language reason.",
      ],
      questions: [
        "Is this the right issued ticket?",
        "Is it still inside the void window?",
        "Should Intraverse take this void, or decline it?",
      ],
      showAccept: true,
    },
  };

  return { ...shared, ...(copies[trigger] || copies.needs_review) };
}

function getVoidStatusOverlay(data, { stage }) {
  const code = normalizeVoidStatus(data?.status);
  const note = voidStatusNote(data);
  const noteLine = note ? ` ${note}` : "";
  const reason = data?.rejectReason || data?.reason || "";

  const overlays = {
    voidrequestsubmitted: {
      statusLabel: "Request received",
      title: "Void request received",
      description:
        "Validation passed. Processing can start automatically or go to Ops. Do not credit the agent wallet yet.",
      tone: "action",
    },
    voidrequestvalidationfailed: {
      statusLabel: "Could not start",
      title: "This void could not be started",
      description: `A system check failed before anything was sent.${noteLine} No money moved. Close this request.`,
      tone: "danger",
    },
    voidrequestcanceled: {
      statusLabel: "Agent withdrew",
      title: "The agent canceled this void request",
      description:
        "The agent canceled / withdrew this void. No airline void was completed. The original ticket stays as it is.",
      tone: "info",
    },
    voidrequestcancelled: {
      statusLabel: "Agent withdrew",
      title: "The agent canceled this void request",
      description:
        "The agent canceled / withdrew this void. No airline void was completed. The original ticket stays as it is.",
      tone: "info",
    },
    voidrequestdenied: isVoidAirlineDeclinedReason(data)
      ? {
          statusLabel: "Airline declined void",
          title: "The airline declined this void",
          description:
            "The airline said no. The ticket stays issued. Nothing comes off the agent wallet. Point the agent to a refund if they still need money back.",
          tone: "danger",
        }
      : {
          statusLabel: "Declined by Ops",
          title: "Void request declined",
          description: reason
            ? `Ops declined this void. Reason: ${reason}. The original ticket stays as it is.`
            : "Ops declined this void. The original ticket stays as it is.",
          tone: "danger",
        },
    denied: isVoidAirlineDeclinedReason(data)
      ? {
          statusLabel: "Airline declined void",
          title: "The airline declined this void",
          description:
            "The airline said no. The ticket stays issued. Nothing comes off the agent wallet.",
          tone: "danger",
        }
      : {
          statusLabel: "Declined by Ops",
          title: "Void request declined",
          description: reason
            ? `Ops declined this void. Reason: ${reason}. The original ticket stays as it is.`
            : "Ops declined this void. The original ticket stays as it is.",
          tone: "danger",
        },
    rejected: isVoidAirlineDeclinedReason(data)
      ? {
          statusLabel: "Airline declined void",
          title: "The airline declined this void",
          description:
            "The airline said no. The ticket stays issued. Nothing comes off the agent wallet.",
          tone: "danger",
        }
      : {
          statusLabel: "Declined by Ops",
          title: "Void request declined",
          description: reason
            ? `Ops declined this void. Reason: ${reason}. The original ticket stays as it is.`
            : "Ops declined this void. The original ticket stays as it is.",
          tone: "danger",
        },
    voidprocessing: {
      statusLabel: "Sending to airline",
      title: "Sending this void to the airline",
      description:
        "The computer is processing the void. Stay on this request. A timeout is not an airline decline.",
      tone: "wait",
    },
    autovoidprocessing: {
      statusLabel: "Sending to airline",
      title: "The computer is sending this void",
      description:
        "Automatic void is in progress. If this sits too long, take over and send it by hand. A timeout is not an airline decline.",
      tone: "wait",
    },
    autovoidfailed: {
      statusLabel: "Send failed — void it on airline site",
      title: "Automatic send failed. Void it on the airline site",
      description: `Intraverse could not send the void (technical problem). That is not an airline decline.${noteLine} The ticket is still issued. You must submit the void yourself on the airline site.`,
      steps: VOID_AIRLINE_SUBMIT_STEPS,
      tone: "action",
    },
    manualreviewrequired:
      stage === "send_failed" || stage === "needs_manual_void"
        ? {
            statusLabel: "Void it on the airline site",
            title: "Void this ticket on the airline site",
            description: `Auto send already failed or Ops already accepted.${noteLine} Go to the airline site, void it yourself, confirm it shows voided, then finish here.`,
            steps: VOID_AIRLINE_SUBMIT_STEPS,
            tone: "action",
          }
        : {
            statusLabel: "Ops must review this void",
            title: "Ops must review this void",
            description:
              "A person must look at this first. Do not void on the airline site yet. Check the ticket and void window, then Accept or Decline. After Accept, the next screen tells you to submit the void on the airline site.",
            steps: [
              "On this screen: check ticket number, passengers, and void deadline.",
              "Open the airline site only to CHECK eligibility — do not void yet.",
              "If it can be voided: Accept.",
              "If it cannot: Decline and write why.",
            ],
            tone: "action",
          },
    pending: {
      statusLabel: "Ops must review this void",
      title: "Ops must review this void",
      description:
        "A person must look at this first. Do not void on the airline site yet. Check the ticket and void window, then Accept or Decline.",
      steps: [
        "On this screen: check ticket number, passengers, and void deadline.",
        "Open the airline site only to CHECK eligibility — do not void yet.",
        "If it can be voided: Accept.",
        "If it cannot: Decline and write why.",
      ],
      tone: "action",
    },
    pendingvoid: {
      statusLabel: "Ops must review this void",
      title: "Ops must review this void",
      description:
        "A person must look at this first. Do not void on the airline site yet. Check the ticket and void window, then Accept or Decline.",
      steps: [
        "On this screen: check ticket number, passengers, and void deadline.",
        "Open the airline site only to CHECK eligibility — do not void yet.",
        "If it can be voided: Accept.",
        "If it cannot: Decline and write why.",
      ],
      tone: "action",
    },
    manualvoidprocessing: {
      statusLabel: "Void it on the airline site",
      title: "Void this ticket on the airline site now",
      description:
        "Ops accepted this request. Intraverse has not voided the ticket. Open the airline or supplier site, void it there, confirm it shows voided, then finish here.",
      steps: VOID_AIRLINE_SUBMIT_STEPS,
      tone: "action",
    },
    approved: {
      statusLabel: "Void it on the airline site",
      title: "Void this ticket on the airline site now",
      description:
        "The request is accepted. Intraverse has not voided the ticket. Void it on the airline site yourself, confirm it shows voided, then finish here.",
      steps: VOID_AIRLINE_SUBMIT_STEPS,
      tone: "action",
    },
    submittedtosupplier: {
      statusLabel: "Waiting on airline",
      title: "Waiting on the airline — do not submit again",
      description:
        "The void has already been submitted. Do not void it again on the airline site. Wait for the airline answer, or open the site only to check status.",
      steps: VOID_AIRLINE_CHECK_ONLY_STEPS,
      tone: "wait",
    },
    supplierprocessing: {
      statusLabel: "Waiting on airline",
      title: "Airline is still working — do not submit again",
      description:
        "The void was already sent. Do not submit a new void. Wait, or check status on the airline site only. A timeout is not an airline decline.",
      steps: VOID_AIRLINE_CHECK_ONLY_STEPS,
      tone: "wait",
    },
    voidsuccessful: {
      statusLabel: "Airline voided — settle money",
      title: "The airline voided this ticket. Settle the money",
      description:
        "Ticket is voided. Debit the void/service fee, then credit the balance to the agent wallet. If settlement fails, the ticket stays voided — fix money separately.",
      tone: "action",
    },
    voiddeclinedbysupplier: {
      statusLabel: "Airline declined void",
      title: "The airline declined this void",
      description: reason
        ? `The airline said no. Reason: ${reason}. This is not a technical timeout. Close if the ticket stays issued. Settlement may still apply for fee rules.`
        : "The airline declined this void. This is not a technical timeout. Close if the ticket stays issued.",
      tone: "danger",
    },
    voidfailedbysupplier: {
      statusLabel: "Airline could not void it",
      title: "The airline could not void this ticket",
      description: reason
        ? `The airline received the request but could not void it. Reason: ${reason}. This is not a timeout.`
        : "The airline received the request but could not void it (window expired, status changed, or host reject). This is not a timeout.",
      tone: "danger",
    },
    settlementpending: {
      statusLabel: "Settling money",
      title: "Settlement is running",
      description:
        "Debit the void/service fee, then credit the applicable balance to the agent wallet. Do not un-void the ticket if money fails.",
      tone: "action",
    },
    servicefeedebited: {
      statusLabel: "Fee taken — credit balance",
      title: "Service fee has been taken",
      description:
        "Credit the applicable balance to the agent wallet next. Then this should complete.",
      tone: "action",
    },
    balancerefunded: {
      statusLabel: "Balance credited",
      title: "Agent wallet has been credited",
      description:
        "The applicable balance was credited. This should move to completed (or failure settled if the airline did not void).",
      tone: "action",
    },
    settlementfailed: {
      statusLabel: "Airline done — money failed",
      title: "Wallet settlement failed",
      description:
        "The airline outcome stays as it is. Retry crediting the agent wallet. Do not change a successful void back to failed.",
      tone: "danger",
    },
    voidcompleted: {
      statusLabel: "Completed",
      title: "Void completed",
      description:
        "The ticket is voided and the agent wallet has been settled. No further action is needed.",
      tone: "success",
    },
    voidfailuresettled: {
      statusLabel: "Auto submission failed",
      title: "Auto submission failed",
      description:
        "Automatic void submission failed and this request is settled. No further action is needed. Check the supplier response for details.",
      tone: "action",
    },
    success: {
      statusLabel: "Completed",
      title: "Void completed",
      description:
        "The ticket is voided and settlement is done. No further action is needed.",
      tone: "success",
    },
    closed: {
      statusLabel: "Completed",
      title: "Void completed",
      description:
        "The ticket is voided and settlement is done. No further action is needed.",
      tone: "success",
    },
  };

  return overlays[code] || null;
}

export function getVoidStatusLabelForCode(status) {
  const overlay = getVoidStatusOverlay({ status }, { stage: "" });
  if (overlay?.statusLabel) return overlay.statusLabel;
  const fallback = {
    pending: "Ops must review this void",
    pendingvoid: "Ops must review this void",
    manualreviewrequired: "Ops must review this void",
    failed: "Airline declined void",
    voidrequestdenied: "Declined by Ops",
    voidrequestcanceled: "Agent withdrew",
    voidrequestcancelled: "Agent withdrew",
    voidfailuresettled: "Auto submission failed",
    denied: "Declined by Ops",
    rejected: "Declined by Ops",
    cancelled: "Cancelled",
    canceled: "Cancelled",
  };
  return fallback[normalizeVoidStatus(status)] || null;
}

export function getVoidAdminGuidance(data) {
  const stage = getVoidAdminStage(data);
  const reason = data?.rejectReason || data?.reason || "";
  const assignLabel = getVoidAssignActionLabel(data);
  const manualLabel = getVoidManualActionLabel(data);
  const declineLabel = getVoidDeclineActionLabel(data);

  const guidance = {
    needs_manual_review: {
      title: "Ops must review this void",
      description:
        "A person must look at this first. Do not void on the airline site yet. Check the ticket and void window, then Accept or Decline. After Accept, you will be told to submit the void on the airline site.",
      statusLabel: "Ops must review this void",
      tone: "action",
      steps: [
        "On this screen: check ticket number, passengers, and void deadline.",
        "Open the airline site only to CHECK eligibility — do not void yet.",
        "If it can be voided: Accept.",
        "If it cannot: Decline and write why.",
      ],
      primary: [
        { key: VOID_ACTION.review, label: "Review this request" },
        ...(canRetryAutoVoid(data)
          ? [
              {
                key: VOID_ACTION.voidTicket,
                label: getVoidSubmitActionLabel(),
              },
            ]
          : []),
        { key: VOID_ACTION.assign, label: assignLabel },
      ],
      secondary: [],
    },
    auto_processing: {
      title: "The computer is sending this void",
      description:
        "Intraverse is trying to submit the void automatically. Do not submit on the airline site yet. If this sits too long, take over and void it yourself on the airline site. A timeout is not an airline decline.",
      statusLabel: "Sending to airline",
      tone: "wait",
      steps: [
        "Wait a short time for the automatic send.",
        "If it is stuck: open the airline site, void it yourself, confirm it shows voided, then come back.",
        `Click "${manualLabel}" to finish here and pay the agent.`,
      ],
      primary: [
        { key: VOID_ACTION.manualVoid, label: manualLabel },
      ],
      secondary: [{ key: VOID_ACTION.assign, label: assignLabel }],
    },
    needs_manual_void: {
      title: "Void this ticket on the airline site now",
      description:
        "Intraverse has not voided this ticket. Open the airline or supplier site, void it there yourself, confirm it shows voided, then come back and finish here. That pays the agent.",
      statusLabel: "Void it on the airline site",
      tone: "action",
      steps: VOID_AIRLINE_SUBMIT_STEPS,
      primary: [
        { key: VOID_ACTION.manualVoid, label: manualLabel },
      ],
      secondary: [
        { key: VOID_ACTION.assign, label: assignLabel },
        { key: VOID_ACTION.decline, label: declineLabel },
      ],
    },
    send_failed: {
      title: "Automatic send failed. Void it on the airline site",
      description: data?.manualInterventionReason
        ? `${data.manualInterventionReason}. That is a technical problem, not an airline decline. The ticket is still issued. Void it yourself on the airline site, then finish here.`
        : "Automatic void send failed. That is a technical problem, not an airline decline. The ticket is still issued. Void it yourself on the airline site, then finish here.",
      statusLabel: "Send failed — void it on airline site",
      tone: "action",
      steps: VOID_AIRLINE_SUBMIT_STEPS,
      primary: [
        { key: VOID_ACTION.manualVoid, label: manualLabel },
      ],
      secondary: [
        { key: VOID_ACTION.assign, label: assignLabel },
        { key: VOID_ACTION.decline, label: declineLabel },
      ],
    },
    awaiting_supplier: {
      title: "Waiting on the airline — do not submit again",
      description:
        "The void was already submitted. Do not submit a new void on the airline site. Wait for the airline answer, or check status only. Do not credit the agent wallet yet.",
      statusLabel: "Waiting on airline",
      tone: "wait",
      steps: VOID_AIRLINE_CHECK_ONLY_STEPS,
      primary: [],
      secondary: [
        { key: VOID_ACTION.manualVoid, label: manualLabel },
        { key: VOID_ACTION.assign, label: assignLabel },
      ],
    },
    settlement_pending: {
      title: "Airline voided the ticket. Settle the money",
      description:
        "The airline already voided it. Debit the void/service fee, then credit the balance to the agent wallet. Do not void again on the airline site.",
      statusLabel: "Airline voided — settle money",
      tone: "action",
      steps: [
        "Do not open the airline site to void again — it is already voided.",
        "Credit the agent wallet (fee rules apply).",
        "If money fails, the ticket stays voided — fix money separately.",
      ],
      primary: [{ key: VOID_ACTION.manualVoid, label: manualLabel }],
      secondary: [{ key: VOID_ACTION.assign, label: assignLabel }],
    },
    completed: {
      title: "Void completed",
      description:
        "The ticket is voided and the agent wallet has been settled. No further action is needed.",
      statusLabel: "Completed",
      tone: "success",
      steps: [],
      primary: [],
      secondary: [],
    },
    declined: isVoidAirlineDeclinedReason(data)
      ? {
          title: "The airline declined this void",
          description:
            "The airline said no. The ticket stays issued. Nothing comes off the agent wallet. The agent should look at a refund if they still need money back.",
          statusLabel: "Airline declined void",
          tone: "danger",
          steps: [],
          primary: [],
          secondary: [],
        }
      : {
          title: "Void request declined",
          description: reason
            ? `Ops declined this void. Reason: ${reason}. The original ticket stays as it is.`
            : "Ops declined this void. The original ticket stays as it is.",
          statusLabel: "Declined by Ops",
          tone: "danger",
          steps: [],
          primary: [],
          secondary: [],
        },
    canceled: {
      title: "The agent canceled this void request",
      description:
        "The agent canceled / withdrew this void. No airline void was completed. The original ticket stays as it is.",
      statusLabel: "Agent withdrew",
      tone: "info",
      steps: [],
      primary: [],
      secondary: [],
    },
    supplier_failed: {
      title: "The airline did not void this ticket",
      description: reason
        ? `The airline declined or failed this void. Reason: ${reason}. This is not a technical timeout. Close this request if the ticket stays issued.`
        : "The airline declined or failed this void. This is not a technical timeout. Close this request if the ticket stays issued.",
      statusLabel: "Airline declined void",
      tone: "danger",
      steps: [
        "Read the airline reason.",
        "Close this request if the ticket stays issued.",
        'Only use "Already voided on airline — finish here" if you later confirm the airline did void it after all.',
      ],
      primary: [{ key: VOID_ACTION.decline, label: declineLabel }],
      secondary: [
        { key: VOID_ACTION.manualVoid, label: manualLabel },
        { key: VOID_ACTION.assign, label: assignLabel },
      ],
    },
    failure_settled: {
      title: "Auto submission failed",
      description:
        "Automatic void submission failed and this request is settled. No further action is needed. Check the supplier response for details.",
      statusLabel: "Auto submission failed",
      tone: "action",
      steps: [],
      primary: [],
      secondary: [],
    },
    settlement_failed: {
      title: "Wallet settlement failed",
      description:
        "The airline outcome stays as it is. Retry crediting the agent wallet. Do not change a successful void back to failed. Do not void again on the airline site.",
      statusLabel: "Airline done — money failed",
      tone: "danger",
      steps: [
        "Do not void again on the airline site.",
        "Retry crediting the agent wallet.",
        "The ticket stays voided even if money fails.",
      ],
      primary: [{ key: VOID_ACTION.manualVoid, label: manualLabel }],
      secondary: [{ key: VOID_ACTION.assign, label: assignLabel }],
    },
    validation_failed: {
      title: "This void could not be started",
      description:
        "Validation failed before anything was sent to the airline. No money moved. Close this request, then the agent can submit again if it is still eligible.",
      statusLabel: "Could not start",
      tone: "danger",
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "Do not void on the airline site for this failed request.",
      ],
      primary: [{ key: VOID_ACTION.decline, label: declineLabel }],
      secondary: [{ key: VOID_ACTION.assign, label: assignLabel }],
    },
    unknown: {
      title: "Void request",
      description:
        "Open the available actions below. If you are unsure, assign this to a teammate rather than guessing.",
      statusLabel: data?.status || "Unknown",
      tone: "info",
      steps: [],
      primary: [{ key: VOID_ACTION.assign, label: assignLabel }],
      secondary: [],
    },
  };

  const result = {
    stage,
    ...(guidance[stage] || guidance.unknown),
  };
  const overlay = getVoidStatusOverlay(data, { stage });
  if (overlay) {
    if (overlay.title) result.title = overlay.title;
    if (overlay.description) result.description = overlay.description;
    if (overlay.statusLabel) result.statusLabel = overlay.statusLabel;
    if (overlay.tone) result.tone = overlay.tone;
    if (overlay.steps) result.steps = overlay.steps;
  }
  return result;
}

export function canCheckVoidMessages(data) {
  if (!data) return false;
  return Boolean(data?._id);
}

export function getVoidMessageCount(data) {
  const raw = data?.conversation;
  if (!Array.isArray(raw)) return 0;
  return raw.filter(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      Object.prototype.hasOwnProperty.call(entry, "message"),
  ).length;
}

export function getVoidOverflowMenuActions(data) {
  const stage = getVoidAdminStage(data);
  const guidance = getVoidAdminGuidance(data);
  const items = [];

  function push(action) {
    if (!action?.key || items.some((item) => item.key === action.key)) return;
    items.push(action);
  }

  // ManualReviewRequired / Pending: slim list — Accept/Decline are inside Review.
  // View Request is added by FlightMenu above these items.
  if (stage === "needs_manual_review") {
    if (needsVoidReview(data)) {
      push({ key: VOID_ACTION.review, label: "Review this request" });
    }
    if (canRetryAutoVoid(data)) {
      push({
        key: VOID_ACTION.voidTicket,
        label: getVoidSubmitActionLabel(),
      });
    }
    if (canCheckVoidMessages(data)) {
      const count = getVoidMessageCount(data);
      push({
        key: VOID_ACTION.messages,
        label: count > 0 ? `Check messages (${count})` : "Check messages",
      });
    }
    if (canAssignVoidRequest(data)) {
      push({
        key: VOID_ACTION.assign,
        label: getVoidAssignActionLabel(data),
      });
    }
    if (hasVoidSupplierResponse(data)) {
      push({
        key: VOID_ACTION.supplierResponse,
        label: "Supplier Response",
      });
    }
    return items;
  }

  // Messages sit just under "View Request" in the list menu.
  if (canCheckVoidMessages(data)) {
    const count = getVoidMessageCount(data);
    push({
      key: VOID_ACTION.messages,
      label: count > 0 ? `Check messages (${count})` : "Check messages",
    });
  }

  if (hasVoidSupplierResponse(data)) {
    push({
      key: VOID_ACTION.supplierResponse,
      label: "Supplier Response",
    });
  }

  [...(guidance.primary || []), ...(guidance.secondary || [])].forEach(push);

  if (needsVoidReview(data)) {
    push({ key: VOID_ACTION.review, label: "Review this request" });
  }
  if (canRetryAutoVoid(data)) {
    push({
      key: VOID_ACTION.voidTicket,
      label: getVoidSubmitActionLabel(),
    });
  }
  if (canManualVoidTicket(data)) {
    push({
      key: VOID_ACTION.manualVoid,
      label: getVoidManualActionLabel(data),
    });
  }
  if (canAirlineDeclineVoid(data)) {
    push({
      key: VOID_ACTION.airlineDeclined,
      label: getVoidAirlineDeclinedActionLabel(),
    });
  }
  if (canDeclineVoidRequest(data)) {
    push({
      key: VOID_ACTION.decline,
      label: getVoidDeclineActionLabel(data),
    });
  }
  if (canAssignVoidRequest(data)) {
    push({
      key: VOID_ACTION.assign,
      label: getVoidAssignActionLabel(data),
    });
  }
  if (canAddVoidRemark(data)) {
    push({ key: VOID_ACTION.remark, label: "Add remark" });
  }

  if (!items.length && stage === "unknown") {
    push({
      key: VOID_ACTION.assign,
      label: getVoidAssignActionLabel(data),
    });
  }

  return items;
}

export function getVoidSubmitWarningCopy() {
  return {
    phase: "warn",
    kind: "submit_warn",
    title: "Retry automatic void submission?",
    text: "This asks Intraverse to send the void to the airline again automatically (same Auto path that failed or stalled). It does not void the ticket by hand on the airline site, and it does not pay the agent. Stay on this screen while it runs. A timeout here is not an airline decline — if auto fails again, Accept and void it yourself on the airline site.",
  };
}

export function getVoidSubmitCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "submitted",
      title: "Automatic void send started again",
      text: "Intraverse is retrying the automatic void. Wait for the status to update. Do not credit the agent wallet yet. Do not submit a second void on the airline site unless auto fails again.",
    };
  }
  return {
    phase: "error",
    kind: "submit_failed",
    title: "Automatic void retry failed",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. You can try auto again.`,
  };
}

export function getVoidManualWarningCopy(data) {
  const stage = getVoidAdminStage(data);
  if (isVoidWalletCreditStage(data)) {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "This credits the agent wallet",
      text: "The airline already voided this ticket. This deposits the money into the agent's wallet. That payment cannot be reversed from here. The ticket stays voided even if this money step fails.",
    };
  }
  if (stage === "supplier_failed") {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "Only continue if the airline actually voided this",
      text: "The airline recorded a fail or decline. This action still marks the ticket as voided and pays the agent. That money cannot be reversed from here. Only continue if you opened the airline site and the ticket truly shows voided.",
    };
  }
  if (stage === "awaiting_supplier") {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "Only if the airline already shows voided",
      text: "Use this only if you checked the airline site and the ticket is already voided, but this request is stuck waiting. It finishes the request and pays the agent. That payment cannot be reversed from here.",
    };
  }
  return {
    phase: "warn",
    kind: "manual_warn",
    title: "Only if the airline already shows voided",
    text: 'Do not use this instead of submitting the void. First void it on the airline site. Use this only when the airline already shows the ticket as voided. It finishes the request and pays the agent. That payment cannot be reversed from here.',
  };
}

export function getVoidManualCopy(res, data) {
  const msg = String(res?.msg || "");
  const walletStage = isVoidWalletCreditStage(data);
  if (res?.return) {
    return {
      phase: "success",
      kind: "manualed",
      title: walletStage
        ? "The agent wallet has been credited"
        : "The void is complete and the agent has been paid",
      text: walletStage
        ? "The wallet settlement has been recorded. Open the latest status next."
        : "The ticket is marked voided and the agent wallet has been credited. Open the latest status next.",
    };
  }
  return {
    phase: "error",
    kind: "manual_failed",
    title: walletStage
      ? "We could not credit the agent wallet"
      : "We could not complete this manual void",
    text: walletStage
      ? `${msg || "The request failed or timed out"}. The ticket stays voided. Try again. Do not change this back to a failed void.`
      : `${msg || "The request failed or timed out"}. Try again, or confirm sent to airline if you still need to submit it to the airline.`,
  };
}

export function getVoidManualLoadingTitle(data) {
  return isVoidWalletCreditStage(data)
    ? "Crediting the agent wallet"
    : "Recording the manual void";
}

export function getVoidAirlineDeclinedWarningCopy() {
  return {
    phase: "warn",
    kind: "airline_decline_warn",
    title: "Record that the airline declined this void?",
    text: "Only continue if you checked the airline site and the airline said no. This closes the void request. The ticket stays issued. Nothing comes off the agent wallet. The agent will see “The airline declined this void” and a next step toward a refund. Do not use this for an Ops-only decline — use Decline request for that.",
  };
}

export function getVoidAirlineDeclinedCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "airline_declined",
      title: "Recorded as airline declined",
      text: "The void request is closed. The ticket stays issued and no money moved. The agent can see the airline decline and start a refund if needed.",
    };
  }
  return {
    phase: "error",
    kind: "airline_decline_failed",
    title: "We could not record the airline decline",
    text: `${msg || "The request failed or timed out"}. Try again. That is not an airline decline by itself.`,
  };
}
